import { AdminBoardConfig, AvailableField } from '../api/asanaBoard';

// "Copy settings from another client" on the board config screen.
//
// Pure: takes the form's current config, another client's saved config and the
// target project's fields, and returns what the form would become. It never
// saves. The screen fills the form with `next` and the admin still presses
// Save, which goes through the normal PUT and boardConfigIssues validation, so
// nothing here is a second source of truth for what a valid config is.
//
// What is NOT copied, because each is per board and must be chosen for the
// target: workspaceGid, projectGid, and intakeSectionGid (a section belongs to
// exactly one project). displayName is left alone too — it is the label this
// client's sidebar shows.
//
// Everything that names a field or option is checked against the target
// project's fields (`available`). A setting whose GID is not on the target is
// skipped and reported rather than copied: a GID from another board saves
// without complaint and then silently matches nothing. Settings that only
// make sense together (visibility field + its two options; epic field + option;
// request type field + fallback option + offered types) are copied whole or not
// at all, because half of any of them is a state boardConfigIssues refuses.

export interface SettingChange {
  label: string;
  before: string;
  after: string;
}

export interface SkippedSetting {
  setting: string;
  reason: string;
}

export interface CopyResult {
  next: AdminBoardConfig;
  changes: SettingChange[];
  skipped: SkippedSetting[];
  // Things the admin still has to do or decide, not failures.
  notes: string[];
}

const NONE = '—';

export function copyBoardSettings(
  target: AdminBoardConfig,
  source: AdminBoardConfig,
  available: AvailableField[],
): CopyResult {
  const byGid = new Map(available.map((f) => [f.fieldGid, f]));
  const optionsOf = (fieldGid: string | null) =>
    new Set((fieldGid ? byGid.get(fieldGid)?.options : undefined)?.map((o) => o.gid) ?? []);
  const skipped: SkippedSetting[] = [];
  const notes: string[] = [];

  const next: AdminBoardConfig = {
    ...target,
    commentsEnabled: source.commentsEnabled,
    showFieldHistory: source.showFieldHistory,
    taskCreationEnabled: source.taskCreationEnabled,
  };

  // ---- fields shown to the client ------------------------------------------
  const rows: AdminBoardConfig['fields'] = [];
  for (const row of [...source.fields].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (byGid.has(row.fieldGid)) rows.push(row);
    else if (row.mode !== 'hidden') {
      skipped.push({ setting: `Field “${row.label}” (${row.mode})`, reason: 'not on this project' });
    }
  }
  next.fields = rows.map((f, i) => ({ ...f, sortOrder: i }));

  // ---- client visibility: the field and the two options, as one ------------
  const visOptions = optionsOf(source.visibilityFieldGid);
  if (!byGid.has(source.visibilityFieldGid)) {
    skipped.push({ setting: 'Client Visibility field', reason: 'not on this project; left as it was' });
  } else if (!visOptions.has(source.viewOptionGid) || !visOptions.has(source.discussOptionGid)) {
    skipped.push({
      setting: 'Client Visibility field',
      reason: 'its “view” and “discuss” options are not on this project’s copy of the field; left as it was',
    });
  } else {
    next.visibilityFieldGid = source.visibilityFieldGid;
    next.viewOptionGid = source.viewOptionGid;
    next.discussOptionGid = source.discussOptionGid;
  }

  // ---- epic ----------------------------------------------------------------
  if (!source.epicFieldGid) {
    next.epicFieldGid = null;
    next.epicOptionGid = null;
  } else if (!byGid.has(source.epicFieldGid)
    || (source.epicOptionGid && !optionsOf(source.epicFieldGid).has(source.epicOptionGid))) {
    skipped.push({ setting: 'Epic field', reason: 'not on this project; left as it was' });
  } else {
    next.epicFieldGid = source.epicFieldGid;
    next.epicOptionGid = source.epicOptionGid;
  }

  // ---- request types -------------------------------------------------------
  if (!source.requestTypeFieldGid) {
    next.requestTypeFieldGid = null;
    next.requestTypeQuestionOptionGid = null;
    next.requestTypes = [];
  } else {
    const typeOptions = optionsOf(source.requestTypeFieldGid);
    if (!byGid.has(source.requestTypeFieldGid)) {
      skipped.push({ setting: 'Request Type field and its options', reason: 'field not on this project; left as it was' });
    } else if (!source.requestTypeQuestionOptionGid || !typeOptions.has(source.requestTypeQuestionOptionGid)) {
      skipped.push({
        setting: 'Request Type field and its options',
        reason: 'the fallback (Question) option is not on this project’s copy of the field; left as it was',
      });
    } else {
      next.requestTypeFieldGid = source.requestTypeFieldGid;
      next.requestTypeQuestionOptionGid = source.requestTypeQuestionOptionGid;
      const offered = [...(source.requestTypes ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
      const kept = offered.filter((t) => typeOptions.has(t.optionGid));
      for (const t of offered) {
        if (!typeOptions.has(t.optionGid)) {
          skipped.push({ setting: `Request type “${t.label}”`, reason: 'option not on this project’s Request Type field' });
        }
      }
      next.requestTypes = kept.map((t, i) => ({ ...t, sortOrder: i }));
    }
  }

  // ---- grouping ------------------------------------------------------------
  // Only over a field the client can see (boardConfigIssues), so it is judged
  // against the field rows the form will have, not the source's.
  const shown = (gid: string) => next.fields.some((f) => f.fieldGid === gid && f.mode !== 'hidden');
  if (!source.boardGroupFieldGid) {
    next.boardGroupFieldGid = null;
  } else if (byGid.get(source.boardGroupFieldGid)?.subtype === 'enum' && shown(source.boardGroupFieldGid)) {
    next.boardGroupFieldGid = source.boardGroupFieldGid;
  } else {
    skipped.push({
      setting: 'Group the Board view by',
      reason: 'that field is not on this project, or is not shown to the client here; left as it was',
    });
  }
  // The fields table was replaced, so the form's own grouping field may have
  // gone with it. Saving would refuse it; clear it here and say so.
  if (next.boardGroupFieldGid && !shown(next.boardGroupFieldGid)) {
    next.boardGroupFieldGid = null;
    notes.push('The Board view was grouped by a field that is no longer shown to the client, so it is back to grouping by Asana section.');
  }

  if (next.taskCreationEnabled && !next.intakeSectionGid) {
    notes.push('Raising items is on but this board has no intake section yet — choose one below before saving.');
  }

  return { next, changes: diffSettings(target, next, available), skipped, notes };
}

// Human-readable differences between two configs, for the preview. Only
// settings that actually change appear, so an empty list means "nothing to do".
export function diffSettings(
  before: AdminBoardConfig,
  after: AdminBoardConfig,
  available: AvailableField[],
): SettingChange[] {
  const names = new Map<string, string>();
  for (const f of available) {
    names.set(f.fieldGid, f.name);
    for (const o of f.options ?? []) names.set(o.gid, o.name);
  }
  const name = (gid: string | null) => (gid ? names.get(gid) ?? gid : NONE);
  const onOff = (b: boolean) => (b ? 'on' : 'off');

  const out: SettingChange[] = [];
  const push = (label: string, b: string, a: string) => { if (b !== a) out.push({ label, before: b, after: a }); };

  push('Allow client comments', onOff(before.commentsEnabled), onOff(after.commentsEnabled));
  push('“Task updates” log', onOff(before.showFieldHistory), onOff(after.showFieldHistory));
  push('Client Visibility field', name(before.visibilityFieldGid), name(after.visibilityFieldGid));
  push('Option meaning “client can view”', name(before.viewOptionGid), name(after.viewOptionGid));
  push('Option meaning “client can view and comment”', name(before.discussOptionGid), name(after.discussOptionGid));
  push('Epic field', name(before.epicFieldGid), name(after.epicFieldGid));
  push('Option meaning “this is an epic”', name(before.epicOptionGid), name(after.epicOptionGid));
  push('Group the Board view by', name(before.boardGroupFieldGid), name(after.boardGroupFieldGid));
  push('Allow this client to raise a new item', onOff(before.taskCreationEnabled), onOff(after.taskCreationEnabled));
  push('Request Type field', name(before.requestTypeFieldGid), name(after.requestTypeFieldGid));
  push('Request Type “Question” option', name(before.requestTypeQuestionOptionGid), name(after.requestTypeQuestionOptionGid));

  const types = (c: AdminBoardConfig) => [...(c.requestTypes ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((t) => `${t.label} (${name(t.optionGid)})`)
    .join('; ') || NONE;
  push('Types the client can choose between', types(before), types(after));

  const describeRow = (r: { label: string; mode: string }) => `${r.mode}, “${r.label}”`;
  const beforeRows = new Map(before.fields.map((f) => [f.fieldGid, f]));
  const afterRows = new Map(after.fields.map((f) => [f.fieldGid, f]));
  for (const f of after.fields) {
    const was = beforeRows.get(f.fieldGid);
    push(`Field: ${name(f.fieldGid)}`, was ? describeRow(was) : NONE, describeRow(f));
  }
  for (const f of before.fields) {
    if (!afterRows.has(f.fieldGid)) push(`Field: ${name(f.fieldGid)}`, describeRow(f), 'removed');
  }
  // Order among the fields both configs have, so an add or a remove does not
  // read as a reshuffle of the rest.
  const common = (c: AdminBoardConfig, other: Map<string, unknown>) =>
    c.fields.filter((f) => other.has(f.fieldGid)).map((f) => name(f.fieldGid));
  push('Field order', common(before, afterRows).join(', ') || NONE, common(after, beforeRows).join(', ') || NONE);

  return out;
}
